import { useEffect, useState } from "react"
import { AlertTriangle, CheckCircle2, Eye, Loader2, RefreshCw, Send, XCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { producaoApi, errorMessage, type FieldConfig, type FieldEvent, type FieldLog, type Job, type VanStockLine } from "./api"
import { useProducao } from "./context"
import { Pill } from "./components"
import { confirmDialog } from "@/lib/confirm"

/**
 * G4 · «Registo de campo» e «Fecho» — o que a equipa fez na obra, vindo da app
 * de campo (Fase 3): a linha temporal dos eventos com as fotos, e o fecho com
 * a checklist, a assinatura, a configuração deixada ao cliente (password
 * escondida, «Mostrar» fica registado) e o PDF de instruções com «Reenviar».
 */

const EVENT_LABEL: Record<string, string> = {
  "carga.linha": "Leitura na carga",
  "carga.confirmada": "Carga confirmada",
  "obra.iniciada": "Obra começada",
  "material.usado": "Material usado",
  "material.extra": "Material extra",
  "serie.instalada": "Série instalada",
  "media.anexada": "Fotografia/vídeo",
  "obra.suspensa": "Obra suspensa",
  "obra.fecho": "Fecho",
  "obra.terminada": "Obra terminada",
  "carrinha.devolucao": "Devolução ao armazém",
  "carrinha.movimento": "Movimento fora de obra",
  "carrinha.contagem": "Contagem da carrinha",
  // Ledger das escritas no contrato do Koortex (modo `host`).
  "contrato.create": "Contrato criado no Koortex",
  "contrato.award": "Contrato · adjudicação",
  "contrato.plan": "Contrato · dono e data",
  "contrato.advance": "Contrato · fase seguinte",
  "contrato.progress": "Contrato · ponto de situação",
  "contrato.checklist": "Contrato · lista da instalação",
  "contrato.acceptance": "Contrato · aceitação do cliente",
  "contrato.hold": "Contrato · parado",
  "contrato.hold_change": "Contrato · motivo da paragem",
  "contrato.resume": "Contrato · retomado",
  "contrato.execution.projects": "Contrato · horas (obra)",
  "contrato.execution.tasks": "Contrato · horas (fase)",
  "contrato.execution.worklogs": "Contrato · horas por técnico",
}

const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })

export function useFieldLog(jobId: string) {
  const { shell } = useProducao()
  const [log, setLog] = useState<FieldLog | null>(null)
  const [error, setError] = useState<string | null>(null)
  const reload = async () => {
    if (!shell) return
    try {
      setLog(await producaoApi.fieldLog(shell, jobId))
      setError(null)
    } catch (err) {
      setError(errorMessage(err))
    }
  }
  useEffect(() => {
    void reload()
  }, [shell, jobId]) // eslint-disable-line react-hooks/exhaustive-deps
  return { log, error, reload }
}

function describe(e: FieldEvent): string {
  const d = e.data as Record<string, any>
  switch (e.type) {
    case "obra.suspensa":
      return String(d.reason ?? "")
    case "material.usado":
    case "material.extra":
      return `Artigo ${d.odoo_product_id} · ${d.qty}${d.reason ? ` · ${d.reason}` : ""}`
    case "serie.instalada":
      return `${d.serial}${d.removed ? " (retirada)" : ""}`
    case "carga.confirmada":
    case "carrinha.devolucao":
    case "carrinha.contagem":
      return `${(d.lines ?? []).length} artigo(s), ${(d.serials ?? []).length} série(s)`
    case "carrinha.movimento":
      return `${d.qty} · ${d.reason} → ${d.destination}`
    case "obra.fecho":
      return d.signed_by ? `Assinado por ${d.signed_by}` : ""
    case "contrato.award":
      return d.external_ref ? `Pagamento ${d.external_ref}` : ""
    case "contrato.checklist":
      return String(d.label ?? "")
    case "contrato.hold":
    case "contrato.hold_change":
      return String(d.note ?? "")
    default:
      return e.type.startsWith("contrato.") && e.host_replayed ? "já estava no Koortex" : ""
  }
}

export function FieldLogTab({ job }: { job: Job }) {
  const { shell, board } = useProducao()
  const { log, error, reload } = useFieldLog(job.id)
  const [busy, setBusy] = useState(false)
  if (error) return <p className="rounded-xl border p-6 text-sm text-destructive border-border">{error}</p>
  if (!log) return <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin" /></div>
  const technicianName = (id: string | null) => board?.technicians.find((t) => t.id === id)?.name ?? "—"
  // As linhas do contrato (`status = contrato`) são registo: o «Tentar de novo» volta a correr o evento do telemóvel.
  const failed = log.events.filter((e) => e.process_status === "erro" && e.status !== "contrato").length
  // A assinatura vai no separador Fecho.
  const gallery = log.media.filter((m) => m.kind !== "assinatura")
  const applyEvent = async (e: FieldEvent) => {
    if (!shell) return
    const msg = `Aplicar «${EVENT_LABEL[e.type] ?? e.type}»? Veio de um técnico que já não estava nesta obra (ou de outra carrinha). Aplicar muda o estado, o Odoo e o fecho como se fosse da equipa.`
    if (!(await confirmDialog(msg, { confirmLabel: "Aplicar" }))) return
    setBusy(true)
    try {
      const r = await producaoApi.applyFieldEvent(shell, job.id, e.uuid)
      shell.bridge.toast(
        r.process_status === "feito"
          ? { variant: "success", title: "Evento aplicado" }
          : { variant: "error", title: "Não ficou aplicado", description: r.process_error ?? r.process_status },
      )
      await reload()
    } catch (err) {
      shell.bridge.toast({ variant: "error", title: "Não foi possível", description: errorMessage(err) })
    } finally {
      setBusy(false)
    }
  }
  const retry = async () => {
    if (!shell) return
    setBusy(true)
    try {
      const r = await producaoApi.retryField(shell, job.id)
      shell.bridge.toast({ variant: "success", title: `${r.retried} evento(s) processados de novo` })
      await reload()
    } catch (err) {
      shell.bridge.toast({ variant: "error", title: "Não foi possível", description: errorMessage(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      {gallery.length > 0 && (
        <section className="space-y-2 rounded-xl border p-4 border-border">
          <h2 className="text-sm font-semibold">Fotografias e vídeos</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {gallery.map((m) => (
              <a key={m.id} href={m.url ?? undefined} target="_blank" rel="noreferrer" className="group block space-y-1">
                <div className="aspect-square overflow-hidden rounded-lg border bg-muted border-border">
                  {m.url && m.kind !== "video" ? (
                    <img src={m.url} alt={m.label ?? m.kind} className="h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                      {m.kind === "video" ? "Vídeo" : "Sem pré-visualização"}
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground group-hover:underline">{m.label ?? m.kind}</p>
              </a>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Os links valem 5 minutos: recarregue o separador para ver de novo.</p>
        </section>
      )}

      <section className="space-y-2 rounded-xl border p-4 border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Linha temporal</h2>
          <div className="flex gap-2">
            {failed > 0 && (
              <Button size="sm" variant="outline" onClick={retry} disabled={busy}>
                <RefreshCw className="h-3.5 w-3.5" /> Tentar de novo ({failed})
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => void reload()}>
              <RefreshCw className="h-3.5 w-3.5" /> Atualizar
            </Button>
          </div>
        </div>
        {log.events.length === 0 ? (
          <p className="text-sm text-muted-foreground">A equipa ainda não registou nada nesta obra.</p>
        ) : (
          <ol className="divide-y divide-border">
            {log.events.map((e) => (
              <li key={e.uuid} className="flex flex-wrap items-start justify-between gap-2 py-2 text-sm">
                <div className="min-w-0">
                  <p className="font-medium">
                    {EVENT_LABEL[e.type] ?? e.type}
                    {e.out_of_assignment && <span className="ml-2"><Pill tone="warning">Fora de atribuição</Pill></span>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {fmtWhen(e.occurred_at)} · {technicianName(e.technician_id)}
                    {describe(e) && ` · ${describe(e)}`}
                    {e.geo && (
                      <>
                        {" · "}
                        <a className="text-accent hover:underline" target="_blank" rel="noreferrer" href={`https://www.google.com/maps/search/?api=1&query=${e.geo.lat},${e.geo.lng}`}>
                          local
                        </a>
                      </>
                    )}
                  </p>
                  {e.process_error && <p className="text-xs text-amber-700">{e.process_error}</p>}
                  {e.reason && <p className="text-xs text-destructive">Recusado: {e.reason}</p>}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {e.odoo_ref && <span className="text-muted-foreground">Odoo {e.odoo_ref}</span>}
                  {e.out_of_assignment && e.status === "aceite" && e.process_status === "ignorado" && (
                    <Button size="sm" variant="outline" className="h-7" onClick={() => applyEvent(e)} disabled={busy}>
                      Aplicar
                    </Button>
                  )}
                  <ProcessPill status={e.status === "recusado" ? "recusado" : e.process_status} />
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  )
}

/**
 * Registo de campo das carrinhas: carga, devolução, movimento e contagem não
 * têm obra, por isso não aparecem em nenhum G4. Aqui vê-se o que ficou fora de
 * atribuição (carrinha que não era a do técnico) ou com erro, com «Aplicar» e
 * «Tentar de novo».
 */
export function VanFieldLog() {
  const { shell, board } = useProducao()
  const [events, setEvents] = useState<Array<FieldEvent & { van_id: string | null }> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const reload = async () => {
    if (!shell) return
    try {
      setEvents((await producaoApi.vanFieldEvents(shell)).events)
      setError(null)
    } catch (err) {
      setError(errorMessage(err))
    }
  }
  useEffect(() => {
    void reload()
  }, [shell]) // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    if (!shell) return
    setBusy(true)
    try {
      await fn()
      shell.bridge.toast({ variant: "success", title: ok })
      await reload()
    } catch (err) {
      shell.bridge.toast({ variant: "error", title: "Não foi possível", description: errorMessage(err) })
    } finally {
      setBusy(false)
    }
  }
  if (error) return <p className="py-24 text-center text-sm text-destructive">{error}</p>
  if (!events) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin" /></div>
  const vanName = (id: string | null) => board?.vans.find((v) => v.id === id)?.name ?? "—"
  const techName = (id: string | null) => board?.technicians.find((t) => t.id === id)?.name ?? "—"
  const failed = events.filter((e) => e.process_status === "erro").length
  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Carrinhas · registo de campo</h1>
          <p className="text-sm text-muted-foreground">Cargas, devoluções, movimentos e contagens. O que é de uma obra está no detalhe dela.</p>
        </div>
        <div className="flex gap-2">
          {failed > 0 && shell && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => producaoApi.retryVanFieldEvents(shell), "Processados de novo")}>
              <RefreshCw className="h-3.5 w-3.5" /> Tentar de novo ({failed})
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => void reload()}>
            <RefreshCw className="h-3.5 w-3.5" /> Atualizar
          </Button>
        </div>
      </header>
      <VanStockCard />
      <section className="rounded-xl border p-4 border-border">
        {events.length === 0 ? (
          <p className="text-sm text-muted-foreground">Ainda não há registos das carrinhas.</p>
        ) : (
          <ol className="divide-y divide-border">
            {events.map((e) => (
              <li key={e.uuid} className="flex flex-wrap items-start justify-between gap-2 py-2 text-sm">
                <div className="min-w-0">
                  <p className="font-medium">
                    {EVENT_LABEL[e.type] ?? e.type} · {vanName(e.van_id)}
                    {e.out_of_assignment && <span className="ml-2"><Pill tone="warning">Fora de atribuição</Pill></span>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {fmtWhen(e.occurred_at)} · {techName(e.technician_id)}
                    {describe(e) && ` · ${describe(e)}`}
                  </p>
                  {e.process_error && <p className="text-xs text-amber-700">{e.process_error}</p>}
                  {e.reason && <p className="text-xs text-destructive">Recusado: {e.reason}</p>}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {e.odoo_ref && <span className="text-muted-foreground">Odoo {e.odoo_ref}</span>}
                  {e.out_of_assignment && e.status === "aceite" && e.process_status === "ignorado" && shell && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7"
                      disabled={busy}
                      onClick={async () => {
                        if (await confirmDialog(`Aplicar «${EVENT_LABEL[e.type] ?? e.type}»? A carrinha não era nenhuma das do técnico nesse dia. Aplicar mexe no Odoo.`, { confirmLabel: "Aplicar" })) {
                          void run(() => producaoApi.applyVanFieldEvent(shell, e.uuid), "Evento aplicado")
                        }
                      }}
                    >
                      Aplicar
                    </Button>
                  )}
                  <ProcessPill status={e.status === "recusado" ? "recusado" : e.process_status} />
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  )
}

const fmtQty = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2))

/**
 * Stock PREVISTO de uma carrinha: o validado no Odoo, mais as cargas
 * preparadas, menos as saídas preparadas (devoluções, movimentos, entregas das
 * obras) — no modo «preparar», o que a equipa tem de facto. O que ainda falta
 * validar no escritório vem assinalado.
 */
function VanStockCard() {
  const { shell, board } = useProducao()
  const vans = (board?.vans ?? []).filter((v) => v.active && v.odoo_location)
  const [vanId, setVanId] = useState<string>("")
  const [lines, setLines] = useState<VanStockLine[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const current = vanId || vans[0]?.id || ""
  useEffect(() => {
    if (!shell || !current) return
    setLines(null)
    setError(null)
    producaoApi.vanStock(shell, current).then((r) => setLines(r.lines)).catch((err) => setError(errorMessage(err)))
  }, [shell, current])
  if (vans.length === 0) return null
  const pending = (lines ?? []).filter((l) => l.qty_pending !== 0 || l.serials_leaving.length > 0).length
  return (
    <section className="space-y-3 rounded-xl border p-4 border-border">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">Stock previsto</h2>
          <p className="text-xs text-muted-foreground">Validado no Odoo + cargas preparadas − saídas preparadas.</p>
        </div>
        <div className="flex items-center gap-2">
          {pending > 0 && <Pill tone="warning">{pending} por validar no escritório</Pill>}
          <select aria-label="Carrinha" className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={current} onChange={(e) => setVanId(e.target.value)}>
            {vans.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {!lines && !error && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>}
      {lines && lines.length === 0 && <p className="text-sm text-muted-foreground">A carrinha está vazia.</p>}
      {lines && lines.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground border-border">
                <th className="py-2 font-medium">Artigo</th>
                <th className="py-2 text-right font-medium">Previsto</th>
                <th className="py-2 text-right font-medium">Validado</th>
                <th className="py-2 text-right font-medium">Por validar</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.odoo_product_id} className="border-b align-top last:border-0 border-border">
                  <td className="py-2">
                    {l.name}
                    {l.default_code && <span className="ml-1 text-xs text-muted-foreground">{l.default_code}</span>}
                    {l.serials.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {l.serials.map((s) => (l.serials_pending.includes(s) ? `${s} (a chegar)` : s)).join(", ")}
                      </p>
                    )}
                    {l.serials_leaving.length > 0 && <p className="text-xs text-amber-700">A sair: {l.serials_leaving.join(", ")}</p>}
                  </td>
                  <td className="py-2 text-right font-medium">{fmtQty(l.qty)} {l.unit}</td>
                  <td className="py-2 text-right text-muted-foreground">{fmtQty(l.qty_validated)}</td>
                  <td className={l.qty_pending !== 0 ? "py-2 text-right text-amber-700" : "py-2 text-right text-muted-foreground"}>
                    {l.qty_pending === 0 ? "—" : `${l.qty_pending > 0 ? "+" : ""}${fmtQty(l.qty_pending)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function ProcessPill({ status }: { status: string }) {
  if (status === "feito") return <Pill tone="success">Processado</Pill>
  if (status === "erro") return <Pill tone="danger">Erro</Pill>
  if (status === "em_curso") return <Pill tone="info">A processar</Pill>
  if (status === "recusado") return <Pill tone="danger">Recusado</Pill>
  if (status === "ignorado") return <Pill tone="neutral">Só registo</Pill>
  return <Pill tone="info">Por processar</Pill>
}

const EMAIL_STATUS: Record<string, { tone: "success" | "warning" | "danger" | "neutral" | "info"; label: string }> = {
  enviado: { tone: "success", label: "Email enviado" },
  por_enviar: { tone: "info", label: "Por enviar" },
  a_enviar: { tone: "info", label: "A enviar" },
  sem_email: { tone: "warning", label: "Sem email do cliente" },
  erro: { tone: "danger", label: "Email falhou" },
  desligado: { tone: "neutral", label: "Envio desligado" },
}

export function ClosureTab({ job }: { job: Job }) {
  const { shell } = useProducao()
  const { log, error, reload } = useFieldLog(job.id)
  const [cfg, setCfg] = useState<FieldConfig | null>(null)
  const [secret, setSecret] = useState<Record<string, string> | null>(null)
  const [email, setEmail] = useState("")
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!shell) return
    producaoApi.fieldConfig(shell).then(setCfg).catch(() => setCfg(null))
  }, [shell])
  useEffect(() => setEmail(log?.closure?.client_email ?? ""), [log?.closure?.client_email])

  if (error) return <p className="rounded-xl border p-6 text-sm text-destructive border-border">{error}</p>
  if (!log) return <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin" /></div>
  const closure = log.closure
  if (!closure) {
    return <p className="rounded-xl border p-6 text-sm text-muted-foreground border-border">A obra ainda não foi fechada na app de campo.</p>
  }

  const signature = log.media.find((m) => m.id === closure.signature_media_id)
  const labelOf = (key: string) => cfg?.client_config_fields.find((f) => f.key === key)?.label ?? key
  const checklistLabel = (id: string) => cfg?.checklist.find((c) => c.id === id)?.label ?? id
  const items = cfg ? cfg.checklist.map((c) => c.id) : Object.keys(closure.checklist)
  const status = EMAIL_STATUS[closure.email_status] ?? EMAIL_STATUS.por_enviar!

  const reveal = async () => {
    if (!shell || !(await confirmDialog("Mostrar a password do cliente? Fica registado quem a viu.", { confirmLabel: "Mostrar" }))) return
    try {
      setSecret((await producaoApi.revealClientConfig(shell, job.id)).fields)
    } catch (err) {
      shell.bridge.toast({ variant: "error", title: "Não foi possível", description: errorMessage(err) })
    }
  }
  const resend = async () => {
    if (!shell) return
    setBusy(true)
    try {
      await producaoApi.resendClosure(shell, job.id, email.trim() ? { email: email.trim() } : {})
      shell.bridge.toast({ variant: "success", title: "Instruções reenviadas" })
      await reload()
    } catch (err) {
      shell.bridge.toast({ variant: "error", title: "Não foi possível reenviar", description: errorMessage(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <section className="space-y-2 rounded-xl border p-4 border-border">
        <h2 className="text-sm font-semibold">Checklist</h2>
        <ul className="space-y-1 text-sm">
          {items.map((id) => (
            <li key={id} className="flex items-center gap-2">
              {closure.checklist[id] ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-label="Feito" />
              ) : (
                <XCircle className="h-4 w-4 text-muted-foreground" aria-label="Não feito" />
              )}
              {checklistLabel(id)}
            </li>
          ))}
        </ul>
        <p className="pt-2 text-xs text-muted-foreground">
          Fechada {fmtWhen(closure.closed_at)} · instruções {closure.instructions_delivered ? "entregues" : "não entregues"}
        </p>
      </section>

      <section className="space-y-2 rounded-xl border p-4 border-border">
        <h2 className="text-sm font-semibold">Assinatura</h2>
        {signature?.url ? (
          <img src={signature.url} alt="Assinatura do cliente" className="max-h-32 rounded border bg-white border-border" />
        ) : (
          <p className="text-sm text-muted-foreground">{closure.signature_media_id ? "A imagem ainda não chegou." : "Sem assinatura."}</p>
        )}
        {!closure.signature_media_id && closure.unsigned_reason && (
          <p className="text-sm text-amber-700">Motivo: {closure.unsigned_reason}</p>
        )}
        <p className="text-sm">{closure.signed_by ?? "—"}</p>
      </section>

      <section className="space-y-2 rounded-xl border p-4 border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Configuração deixada ao cliente</h2>
          {(log.client_config?.secret_keys.length ?? 0) > 0 && !secret && (
            <Button size="sm" variant="ghost" onClick={reveal}>
              <Eye className="h-3.5 w-3.5" /> Mostrar password
            </Button>
          )}
        </div>
        {log.client_config ? (
          <dl className="grid grid-cols-[120px_minmax(0,1fr)] gap-x-2 gap-y-1.5 text-sm sm:grid-cols-[160px_minmax(0,1fr)]">
            {Object.entries(log.client_config.fields).map(([k, v]) => (
              <FragmentRow key={k} label={labelOf(k)} value={v} />
            ))}
            {log.client_config.secret_keys.map((k) => (
              <FragmentRow key={k} label={labelOf(k)} value={secret ? secret[k] ?? "—" : "••••••••"} mono />
            ))}
          </dl>
        ) : (
          <p className="text-sm text-muted-foreground">Sem configuração.</p>
        )}
        {log.client_config && log.client_config.reveals > 0 && (
          <p className="text-xs text-muted-foreground">A password já foi vista {log.client_config.reveals} vez(es).</p>
        )}
      </section>

      <section className="space-y-3 rounded-xl border p-4 border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Instruções (PDF)</h2>
          <Pill tone={status.tone}>{status.label}</Pill>
        </div>
        {closure.pdf_url && (
          <a href={closure.pdf_url} target="_blank" rel="noreferrer" className="text-sm text-accent hover:underline">
            Abrir o PDF
          </a>
        )}
        <p className="text-xs text-muted-foreground">O PDF não leva a password: no sítio dela diz «Entregue em mão».</p>
        {closure.email_error && (
          <p className="flex items-center gap-1 text-xs text-destructive"><AlertTriangle className="h-3.5 w-3.5" /> {closure.email_error}</p>
        )}
        <div className="flex gap-2">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@cliente.pt"
            aria-label="Email do cliente"
          />
          <Button variant="outline" onClick={resend} disabled={busy}>
            <Send className="h-3.5 w-3.5" /> Reenviar
          </Button>
        </div>
        {closure.email_sent_at && <p className="text-xs text-muted-foreground">Último envio {fmtWhen(closure.email_sent_at)}</p>}
      </section>
    </div>
  )
}

function FragmentRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={mono ? "font-mono" : undefined}>{value || "—"}</dd>
    </>
  )
}
